import {
  apaNameInitialsFirst,
  chicagoNoteAuthors,
  naturalName,
  type PersonName,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  get,
  mlaUrl,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  doiUrl,
  publisherName,
  type RichPart,
  type SpecialValues,
  splitPages,
} from "@/lib/tool-kit/special-sources/common";
import { parseSoftwareAuthors } from "@/lib/tool-kit/special-sources/github-citation-generator";

/*
 * The number, title and credit for a figure or table reproduced or adapted from
 * another work: APA 7's copyright attribution note, MLA 9's caption and source line,
 * and Chicago 18's credit line.
 *
 * Sources checked (October 2026):
 * - APA Style, "Figure setup" (Publication Manual §§7.22–7.36): the figure number in
 *   bold ("Figure 1"), the title on the next line in italic title case, notes below.
 *   Copyright attribution (§7.7, §§12.14–12.18; Table 12.1, p. 390, as Augusta
 *   University reproduces it): "From" for a reprint, "Adapted from" for an adaptation;
 *   journal — "“Title of Article,” by A. A. Author and B. B. Author, year, Title of
 *   Periodical, Volume(issue), p. xx (DOI or URL)"; authored book — "Title of Book or
 *   Report (p. xx), by …, year, Publisher (DOI or URL)"; edited book chapter —
 *   "“Title of Chapter,” by …, in E. E. Editor and F. F. Editor (Eds.), Title of Book
 *   (edition, p. xx), year, Publisher (DOI or URL)"; webpage — "Title of Webpage, by
 *   …, year, Site Name (DOI or URL)", without the site name when the author is the
 *   site; then "Copyright year by Name of Copyright Holder.", "In the public domain."
 *   or the Creative Commons licence ("CC BY-NC."), and "Reprinted with permission." or
 *   "Adapted with permission." only if permission was obtained. APA's own sample
 *   figures: "From “Large Continuous Perspective Change With Noncoplanar Points
 *   Enables Accurate Slant Perception,” by X. M. Wang, M. Lind, and G. P. Bingham,
 *   2018, Journal of Experimental Psychology: Human Perception and Performance,
 *   44(10), p. 1513 (https://doi.org/10.1037/xhp0000553). Copyright 2018 by the
 *   American Psychological Association." and "Adapted from 2017 Poverty Rate in the
 *   United States, by U.S. Census Bureau, 2017 (https://www.census.gov/…). In the
 *   public domain." Titles in an attribution are in APA title case, which capitalizes
 *   every word of four letters or more (§6.17).
 * - MLA 9: MLA Style Center, "How should I format captions for figures …?" — "Fig. 1.
 *   Mary Cassatt, Mother and Child, Wichita Art Museum." — and "When should citation
 *   information in captions be separated by commas instead of semicolons?" (December
 *   6, 2018): with full bibliographic details, the periods of a works-cited entry
 *   become semicolons — "Fig. 1. Vincent van Gogh; The Olive Trees; 1889; Museum of
 *   Modern Art, 2001; postcard." A table takes its label and title above and a
 *   "Source:" line below; a caption with complete source information needs no
 *   works-cited entry unless the source is cited in the text (MLA Handbook §1.7).
 * - Chicago 18 §§3.30–3.38 (credit lines) and §3.78 (table source notes): the credit
 *   follows the caption in note form — "Reproduced by permission from Mark Girouard,
 *   Life in the English Country House … (Yale University Press, 1978), 162." (CMOS
 *   specimen; CMOS Q&A on screenshots: "Reproduced by permission from The Chicago
 *   Manual of Style Online, 18th ed. (University of Chicago Press, 2024), 7.96") —
 *   "Adapted from …" for altered material, and "Source: …" under a table.
 */

export const FIGURE_FIELDS = {
  objectKind: "objectKind",
  number: "number",
  figureTitle: "figureTitle",
  noteText: "noteText",
  use: "use",
  sourceType: "sourceType",
  authors: "authors",
  year: "year",
  sourceTitle: "sourceTitle",
  container: "container",
  editors: "editors",
  edition: "edition",
  volume: "volume",
  issue: "issue",
  page: "page",
  publisher: "publisher",
  link: "link",
  status: "status",
  copyrightYear: "copyrightYear",
  copyrightHolder: "copyrightHolder",
  license: "license",
  permission: "permission",
} as const;

const F = FIGURE_FIELDS;

export type FigureSourceType = "article" | "book" | "chapter" | "webpage";
export type CopyrightStatus = "copyright" | "cc" | "public";

/** One labelled line of output. */
export interface FigureEntry {
  label: string;
  parts: RichPart[];
}

export interface FigureOutput {
  key: string;
  label: string;
  entries: FigureEntry[];
  note?: string;
}

export interface FigureLabels {
  apa: string;
  mla: string;
  chicago: string;
  apaNumber: string;
  apaTitle: string;
  apaNote: string;
  mlaCaption: string;
  mlaTableLabel: string;
  mlaTableTitle: string;
  mlaTableSource: string;
  chicagoCaption: string;
  chicagoTableTitle: string;
  chicagoTableSource: string;
  apaGuide: string;
  mlaGuide: string;
  chicagoGuide: string;
}

/* --------------------------------------------------------- APA title case */

/*
 * APA title case (§6.17): capitalize the first word, the first word after a colon,
 * dash or end punctuation, and every word of four letters or more; lowercase short
 * conjunctions, articles and prepositions of three letters or fewer. Like the other
 * title casers here, it only ever capitalizes, so names and acronyms stay as typed.
 */
const APA_MINOR = new Set([
  "and",
  "as",
  "but",
  "for",
  "if",
  "nor",
  "or",
  "so",
  "yet",
  "a",
  "an",
  "the",
  "at",
  "by",
  "in",
  "of",
  "off",
  "on",
  "per",
  "to",
  "up",
  "via",
]);
const RE_SUBTITLE_BREAK = /[:—?!.]$/;
const RE_FIRST_LETTER = /\p{L}/u;
const RE_NOT_LETTER = /[^\p{L}]/gu;

function capitalize(word: string): string {
  const index = word.search(RE_FIRST_LETTER);
  if (index < 0) {
    return word;
  }
  return (
    word.slice(0, index) +
    word.charAt(index).toUpperCase() +
    word.slice(index + 1)
  );
}

function apaWord(word: string, forced: boolean): string {
  return word
    .split("-")
    .map((part, index) => {
      const bare = part.replace(RE_NOT_LETTER, "").toLowerCase();
      if (APA_MINOR.has(bare) && !(forced && index === 0)) {
        return part;
      }
      return capitalize(part);
    })
    .join("-");
}

export function apaTitleCase(text: string): string {
  let force = true;
  return text
    .trim()
    .split(" ")
    .map((word) => {
      const forced = force;
      force = RE_SUBTITLE_BREAK.test(word);
      return apaWord(word, forced);
    })
    .join(" ");
}

/* ------------------------------------------------------------- the source */

interface Source {
  type: FigureSourceType;
  authors: PersonName[];
  editors: PersonName[];
  year: string;
  title: string;
  container: string;
  edition: string;
  volume: string;
  issue: string;
  pages: [string, string];
  publisher: string;
  link: string;
}

const SOURCE_TYPES: readonly FigureSourceType[] = [
  "article",
  "book",
  "chapter",
  "webpage",
];

function readSource(values: SpecialValues): Source {
  const type = get(values, F.sourceType) as FigureSourceType;
  const typed = get(values, F.link);
  return {
    type: SOURCE_TYPES.includes(type) ? type : "article",
    authors: parseSoftwareAuthors(get(values, F.authors)),
    editors: parseSoftwareAuthors(get(values, F.editors)),
    year: get(values, F.year) || "n.d.",
    title: get(values, F.sourceTitle),
    container: get(values, F.container),
    edition: get(values, F.edition),
    volume: get(values, F.volume),
    issue: get(values, F.issue),
    pages: splitPages(get(values, F.page)),
    publisher: get(values, F.publisher),
    link: doiUrl(typed) ?? typed,
  };
}

export function figureIsReady(values: SpecialValues): boolean {
  return get(values, F.sourceTitle) !== "" && get(values, F.number) !== "";
}

function isTable(values: SpecialValues): boolean {
  return get(values, F.objectKind) === "table";
}

function isAdapted(values: SpecialValues): boolean {
  return get(values, F.use) === "adapted";
}

function hasPermission(values: SpecialValues): boolean {
  return get(values, F.permission) === "true";
}

function status(values: SpecialValues): CopyrightStatus {
  const chosen = get(values, F.status) as CopyrightStatus;
  return chosen === "cc" || chosen === "public" ? chosen : "copyright";
}

/* ------------------------------------------------------------------- APA */

/** "A", "A and B", "A, B, and C" — names in an APA attribution, initials first. */
function apaNameList(names: PersonName[]): string {
  const formatted = names.map(apaNameInitialsFirst);
  if (formatted.length <= 2) {
    return formatted.join(" and ");
  }
  return `${formatted.slice(0, -1).join(", ")}, and ${formatted.at(-1)}`;
}

function pageLabel(pages: [string, string]): string {
  if (pages[0] === "") {
    return "";
  }
  return pages[1] ? `pp. ${pages[0]}–${pages[1]}` : `p. ${pages[0]}`;
}

/** "6th ed., p. 2" — the parenthesis after a book title. */
function bookDetails(source: Source): string {
  return [
    source.edition ? `${source.edition} ed.` : "",
    pageLabel(source.pages),
  ]
    .filter(Boolean)
    .join(", ");
}

/** The source as APA's attribution gives it (Table 12.1), after "From". */
function apaAttribution(source: Source): RichPart[] {
  const by =
    source.authors.length > 0 ? `by ${apaNameList(source.authors)}, ` : "";
  const link = source.link ? ` (${source.link})` : "";
  const title = apaTitleCase(source.title);
  const publisher = publisherName(source.publisher, "apa");
  const details = bookDetails(source);
  if (source.type === "article") {
    const page = pageLabel(source.pages);
    return [
      { text: `“${title},” ${by}${source.year}, ` },
      { text: source.container, italic: true },
      { text: source.volume ? ", " : "" },
      { text: source.volume, italic: true },
      {
        text: `${source.issue ? `(${source.issue})` : ""}${page ? `, ${page}` : ""}${link}.`,
      },
    ];
  }
  if (source.type === "book") {
    return [
      { text: title, italic: true },
      {
        text: `${details ? ` (${details})` : ""}, ${by}${source.year}, ${publisher}${link}.`,
      },
    ];
  }
  if (source.type === "chapter") {
    const role = source.editors.length > 1 ? "Eds." : "Ed.";
    const editors =
      source.editors.length > 0
        ? `in ${apaNameList(source.editors)} (${role}), `
        : "in ";
    return [
      { text: `“${title},” ${by}${editors}` },
      { text: apaTitleCase(source.container), italic: true },
      {
        text: `${details ? ` (${details})` : ""}, ${source.year}, ${publisher}${link}.`,
      },
    ];
  }
  const site = source.container ? `, ${source.container}` : "";
  return [
    { text: title, italic: true },
    { text: `, ${by}${source.year}${site}${link}.` },
  ];
}

/** "Copyright 2018 by the American Psychological Association." and the like. */
function apaStatus(values: SpecialValues, source: Source): string {
  const chosen = status(values);
  if (chosen === "public") {
    return "In the public domain.";
  }
  if (chosen === "cc") {
    const license = get(values, F.license) || "CC BY 4.0";
    return `${license}${closer(license)}`;
  }
  const year = get(values, F.copyrightYear) || source.year;
  const holder = get(values, F.copyrightHolder);
  return holder ? `Copyright ${year} by ${holder}.` : `Copyright ${year}.`;
}

function apaOutput(
  values: SpecialValues,
  source: Source,
  labels: FigureLabels
): FigureOutput {
  const word = isTable(values) ? "Table" : "Figure";
  const adapted = isAdapted(values);
  const noteText = get(values, F.noteText);
  const permission = hasPermission(values) && status(values) === "copyright";
  const permissionText = adapted
    ? " Adapted with permission."
    : " Reprinted with permission.";
  const note: RichPart[] = [
    { text: "Note.", italic: true },
    { text: ` ${noteText ? `${noteText}${closer(noteText)} ` : ""}` },
    { text: adapted ? "Adapted from " : "From " },
    ...apaAttribution(source),
    {
      text: ` ${apaStatus(values, source)}${permission ? permissionText : ""}`,
    },
  ];
  const title = get(values, F.figureTitle);
  const entries: FigureEntry[] = [
    {
      label: labels.apaNumber,
      parts: [{ text: `${word} ${get(values, F.number)}`, bold: true }],
    },
  ];
  if (title) {
    entries.push({
      label: labels.apaTitle,
      parts: [{ text: apaTitleCase(title), italic: true }],
    });
  }
  entries.push({ label: labels.apaNote, parts: note });
  return { key: "apa", label: labels.apa, entries, note: labels.apaGuide };
}

/* ------------------------------------------------------------------- MLA */

/** "Xiaoye Michael Wang et al." — names in a caption, in normal order. */
function mlaCaptionNames(names: PersonName[]): string {
  if (names.length >= 3) {
    return `${naturalName(names[0])} et al.`;
  }
  return names.map(naturalName).join(" and ");
}

function mlaLink(link: string): string {
  return link.startsWith("https://doi.org/") ? link : mlaUrl(link);
}

/** The elements of an MLA entry for the source, one group per works-cited "sentence". */
function mlaElements(source: Source): RichPart[][] {
  const title = titleCase(source.title);
  const page = pageLabel(source.pages);
  const publisher = publisherName(source.publisher, "mla");
  if (source.type === "book") {
    return [
      [{ text: title, italic: true }],
      [{ text: [publisher, source.year, page].filter(Boolean).join(", ") }],
    ];
  }
  if (source.type === "chapter") {
    const editors =
      source.editors.length > 0
        ? `edited by ${mlaCaptionNames(source.editors)}`
        : "";
    const edition = source.edition ? `${source.edition} ed.` : "";
    const facts = [editors, edition, publisher, source.year, page]
      .filter(Boolean)
      .join(", ");
    return [
      [{ text: `“${title}”` }],
      [
        { text: titleCase(source.container), italic: true },
        { text: facts ? `, ${facts}` : "" },
      ],
    ];
  }
  if (source.type === "article") {
    const facts = [
      source.volume ? `vol. ${source.volume}` : "",
      source.issue ? `no. ${source.issue}` : "",
      source.year,
      page,
    ]
      .filter(Boolean)
      .join(", ");
    return [
      [{ text: `“${title}”` }],
      [{ text: source.container, italic: true }, { text: `, ${facts}` }],
    ];
  }
  const site = source.container || mlaCaptionNames(source.authors);
  return [
    [{ text: `“${title}”` }],
    [{ text: site, italic: true }, { text: `, ${source.year}` }],
  ];
}

/**
 * The source in full, the periods of its works-cited entry turned into semicolons
 * (MLA Style Center, December 6, 2018). A webpage by the group that runs the site
 * starts with its title, as MLA does when the author is also the publisher.
 */
function mlaCaptionSource(source: Source): RichPart[] {
  const groups: RichPart[][] = [];
  const groupSite =
    source.type === "webpage" &&
    source.container === "" &&
    source.authors.every((name) => name.organization);
  if (source.authors.length > 0 && !groupSite) {
    groups.push([{ text: mlaCaptionNames(source.authors) }]);
  }
  groups.push(...mlaElements(source));
  if (source.link) {
    groups.push([{ text: mlaLink(source.link) }]);
  }
  const joined: RichPart[] = [];
  for (const [index, group] of groups.entries()) {
    if (index > 0) {
      joined.push({ text: "; " });
    }
    joined.push(...group);
  }
  joined.push({ text: "." });
  return joined;
}

function mlaOutput(
  values: SpecialValues,
  source: Source,
  labels: FigureLabels
): FigureOutput {
  const number = get(values, F.number);
  const adapted = isAdapted(values) ? "Adapted from " : "";
  const sourceParts = mlaCaptionSource(source);
  const cc =
    status(values) === "cc"
      ? [{ text: ` ${get(values, F.license) || "CC BY 4.0"}.` }]
      : [];
  if (isTable(values)) {
    // MLA: "Table 1" and the title on separate lines above, the source below.
    const title = titleCase(get(values, F.figureTitle));
    const entries: FigureEntry[] = [
      { label: labels.mlaTableLabel, parts: [{ text: `Table ${number}` }] },
    ];
    if (title) {
      entries.push({ label: labels.mlaTableTitle, parts: [{ text: title }] });
    }
    entries.push({
      label: labels.mlaTableSource,
      parts: [{ text: `Source: ${adapted}` }, ...sourceParts, ...cc],
    });
    return { key: "mla", label: labels.mla, entries, note: labels.mlaGuide };
  }
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      {
        label: labels.mlaCaption,
        parts: [{ text: `Fig. ${number}. ${adapted}` }, ...sourceParts, ...cc],
      },
    ],
    note: labels.mlaGuide,
  };
}

/* --------------------------------------------------------------- Chicago */

/** The source as a Chicago note gives it, without the closing period. */
function chicagoCitation(source: Source): RichPart[] {
  const authors = chicagoNoteAuthors(source.authors);
  const who = authors ? `${authors}, ` : "";
  const title = titleCase(source.title);
  const link = source.link ? `, ${source.link}` : "";
  const page = source.pages[1]
    ? `${source.pages[0]}–${source.pages[1]}`
    : source.pages[0];
  const publisher = publisherName(source.publisher, "chicago");
  const facts = [publisher, source.year].filter(Boolean).join(", ");
  const edition = source.edition ? `, ${source.edition} ed.` : "";
  if (source.type === "article") {
    const volume = [source.volume, source.issue ? `no. ${source.issue}` : ""]
      .filter(Boolean)
      .join(", ");
    return [
      { text: `${who}“${title},” ` },
      { text: source.container, italic: true },
      {
        text: `${volume ? ` ${volume}` : ""} (${source.year})${page ? `: ${page}` : ""}${link}`,
      },
    ];
  }
  if (source.type === "book") {
    return [
      { text: who },
      { text: title, italic: true },
      { text: `${edition} (${facts})${page ? `, ${page}` : ""}${link}` },
    ];
  }
  if (source.type === "chapter") {
    const editors =
      source.editors.length > 0
        ? `, ed. ${chicagoNoteAuthors(source.editors)}`
        : "";
    return [
      { text: `${who}“${title},” in ` },
      { text: titleCase(source.container), italic: true },
      {
        text: `${editors}${edition} (${facts})${page ? `, ${page}` : ""}${link}`,
      },
    ];
  }
  const site = source.container ? `${source.container}, ` : "";
  return [{ text: `${who}“${title},” ${site}${source.year}${link}` }];
}

/** "Reproduced by permission from", "Adapted from", "Source:" — the credit's opening. */
function chicagoLead(values: SpecialValues): string {
  const permission = hasPermission(values) && status(values) === "copyright";
  if (isAdapted(values)) {
    return permission ? "Adapted by permission from " : "Adapted from ";
  }
  return permission ? "Reproduced by permission from " : "Source: ";
}

function chicagoOutput(
  values: SpecialValues,
  source: Source,
  labels: FigureLabels
): FigureOutput {
  const number = get(values, F.number);
  const title = get(values, F.figureTitle);
  const cc =
    status(values) === "cc" ? `, ${get(values, F.license) || "CC BY 4.0"}` : "";
  const credit: RichPart[] = [
    { text: chicagoLead(values) },
    ...chicagoCitation(source),
    { text: `${cc}.` },
  ];
  if (isTable(values)) {
    const lead = isAdapted(values) ? "Source: Adapted from " : "Source: ";
    return {
      key: "chicago",
      label: labels.chicago,
      entries: [
        {
          label: labels.chicagoTableTitle,
          parts: [
            { text: title ? `Table ${number}. ${title}` : `Table ${number}.` },
          ],
        },
        {
          label: labels.chicagoTableSource,
          parts: [
            { text: lead },
            ...chicagoCitation(source),
            { text: `${cc}.` },
          ],
        },
      ],
      note: labels.chicagoGuide,
    };
  }
  const caption = title ? `${title}${closer(title)} ` : "";
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      {
        label: labels.chicagoCaption,
        parts: [{ text: `Figure ${number}. ${caption}` }, ...credit],
      },
    ],
    note: labels.chicagoGuide,
  };
}

export function formatFigure(
  values: SpecialValues,
  labels: FigureLabels
): FigureOutput[] {
  const source = readSource(values);
  return [
    apaOutput(values, source, labels),
    mlaOutput(values, source, labels),
    chicagoOutput(values, source, labels),
  ];
}
